"use client";
import React, { useId } from "react";

import {
  Box,
  BoxInput,
  BoxSx,
  Icon,
  IconName,
  IconNameType,
  Target,
  Text
} from "../../foundations";
import {
  DropdownMenu,
  DropdownMenuProps,
  useDropdownMenu
} from "./DropdownMenu";
import { DropdownOption } from "./DropdownMenu/types";

export interface TextFieldDropdownProps {
  dropdownOptions?: DropdownMenuProps["dropdownOptions"];
  noResultsMessage?: DropdownMenuProps["noResultsMessage"];
  optionDisplayCount?: DropdownMenuProps["optionDisplayCount"];
  onSelectOption?: (option: DropdownOption) => void;
}

export type TextFieldProps = Omit<
  BoxInput,
  "value" | "type" | "placeholder" | "as"
> & {
  label: string;
  max?: number;
  maxLength?: number;
  min?: number;
  value?: string;
  type?: "text" | "email" | "tel" | "password" | "number" | "select";
  error?: string;
  description?: string;
  icon?: IconNameType;
  searchIcon?: () => React.ReactElement;
  onClickClear?: () => void;
  onIconClick?: () => void;
  onEnter?: () => void;
} & TextFieldDropdownProps;

export const TextField: React.FC<TextFieldProps> = ({
  label,
  value,
  error,
  type = "text",
  max,
  maxLength,
  min,
  name,
  description,
  disabled,
  icon,
  readOnly,
  required,
  autoComplete = "off",
  spellCheck = "false",
  dropdownOptions = [],
  noResultsMessage,
  searchIcon,
  optionDisplayCount,
  onClickClear,
  onSelectOption,
  onEnter,
  onBlur,
  onChange,
  onIconClick
}) => {
  const inputId = useId();
  const [isFocused, setIsFocused] = React.useState(false);
  const clearButtonRef = React.useRef<HTMLButtonElement>(null);

  const {
    inputRef,
    focusedOptionIdx,
    handleKeyDown,
    showDropdown,
    iconButtonRef,
    handleClickOption,
    ariaPropsForDropdown,
    handleCompositionStart,
    handleCompositionEnd
  } = useDropdownMenu({
    isSelect: type === "select",
    dropdownOptions,
    onSelectOption,
    isFocused,
    onEnter,
    onClickClear
  });

  const shouldShowLabel = isFocused || !!value;
  const showNoResults =
    noResultsMessage !== undefined &&
    value !== undefined &&
    typeof value === "string" &&
    value.length > 0 &&
    dropdownOptions?.length === 0;

  const handleFocus = () => {
    setIsFocused(true);
  };

  const handleBlur = React.useCallback(
    (event: React.FocusEvent<HTMLInputElement>) => {
      if (
        clearButtonRef.current &&
        event.relatedTarget === clearButtonRef.current &&
        onClickClear
      ) {
        onClickClear();
      }
      handleClickOption(event);
      setIsFocused(false);
      if (onBlur) {
        onBlur(event);
      }
    },
    [onBlur, handleClickOption, onClickClear]
  );

  const handleChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (type === "number") {
        const numberValue = parseInt(e.currentTarget.value, 10);

        if (
          (max !== undefined && numberValue > max) ||
          (min !== undefined && numberValue < min)
        ) {
          // Do not accept the change
        } else {
          onChange?.(e);
        }
      } else {
        onChange?.(e);
      }
    },
    [max, min, onChange, type]
  );

  const handleIconClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (inputRef.current) {
      const inputLength = inputRef.current.value.length;

      if (onIconClick) {
        onIconClick();
      }

      // use setTimeout because focus and cursor position should update after DOM updates
      setTimeout(() => {
        if (inputLength) {
          inputRef.current!.focus();
          inputRef.current!.setSelectionRange(inputLength, inputLength);
        }
      }, 0);
    }
  };

  const labelTextStyles = React.useMemo<BoxSx>(() => {
    const labelYPosition = shouldShowLabel ? "-24px" : "-50%";
    const labelScale = shouldShowLabel ? 0.66 : 1;

    return {
      position: "absolute",
      left: "spacing-4",
      top: "50%",
      transform: `translateY(${labelYPosition}) scale(${labelScale})`,
      transformOrigin: "left center",
      transition: "transition-ease-out-fast",
      pointerEvents: "none",
      fontSize: "font-size-1",
      lineHeight: "leading-2",
      letterSpacing: "kerning-2",
      color: disabled ? "field-text-disabled" : "field-text-label",
      whiteSpace: "nowrap",
      overflow: "hidden"
    };
  }, [disabled, shouldShowLabel]);

  const inputStyles = React.useMemo<BoxSx>(() => {
    return {
      width: "100%",
      height: "spacing-10",
      px: "spacing-4",
      paddingTop: shouldShowLabel ? "spacing-7" : "spacing-5",
      paddingBottom: shouldShowLabel ? "spacing-3" : "spacing-5",
      borderRadius: "radius-2",
      cursor: disabled || readOnly ? "not-allowed" : "auto",
      ...(disabled && { pointerEvents: "none" }),
      // Don't do any special number formatting
      color: "field-text-primary",
      MozAppearance: "textfield",
      overflow: "hidden",
      "::-webkit-outer-spin-button, ::-webkit-inner-spin-button": {
        margin: 0,
        WebkitAppearance: "none"
      }
    };
  }, [shouldShowLabel, disabled, readOnly]);

  const descriptionTextStyles = React.useMemo<BoxSx>(() => {
    return {
      color: disabled ? "field-text-disabled" : "field-text-description"
    };
  }, [disabled]);

  const inputBorderStyles = React.useMemo<BoxSx>(() => {
    return {
      position: "relative",
      borderWidth: "1px",
      borderStyle: "solid",
      borderColor: "input-stroke-enabled",
      outline: "2px solid",
      outlineColor: error
        ? "field-stroke-error"
        : isFocused && !readOnly
          ? "focus"
          : "transparent",
      outlineOffset: "-2px",
      borderRadius: "radius-2",
      marginBottom: error ? "spacing-2" : "spacing-1",
      cursor: disabled || readOnly ? "not-allowed" : "auto",
      backgroundColor:
        disabled || readOnly ? "field-surface-disabled" : "field-surface",
      ...(readOnly && {
        borderWidth: "0 0 1px 0",
        borderRadius: "unset"
      }),
      overflow: "hidden",
      transition: "transition-color-medium"
    };
  }, [disabled, error, isFocused, readOnly]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        userSelect: "none",
        position: "relative"
      }}
    >
      <Box sx={{ ...inputBorderStyles }} {...ariaPropsForDropdown}>
        <Box
          as="label"
          htmlFor={inputId}
          id={`${inputId}-label`}
          sx={{ ...labelTextStyles }}
        >
          <Text variant="body-1-regular">{label}</Text>
          {required && (
            <Text
              variant="body-1-regular"
              sx={{
                color: "feedback-onSurface-incidental-critical"
              }}
            >
              *
            </Text>
          )}
        </Box>
        <Box
          as="input"
          ref={inputRef}
          type={type}
          id={inputId}
          value={value}
          max={max}
          maxLength={maxLength}
          min={min}
          name={name}
          disabled={disabled}
          readOnly={readOnly}
          autoComplete={autoComplete}
          spellCheck={spellCheck}
          aria-disabled={disabled}
          aria-invalid={!!error}
          aria-readonly={readOnly}
          aria-required={required}
          onChange={handleChange}
          onBlur={handleBlur}
          onFocus={handleFocus}
          onKeyDown={handleKeyDown}
          onCompositionStart={handleCompositionStart}
          onCompositionEnd={handleCompositionEnd}
          sx={{ ...inputStyles, justifyContent: "space-between" }}
        />
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            right: searchIcon ? "spacing-2" : "spacing-4",
            transform: "translateY(-50%)",
            display: "flex",
            alignItems: "center",
            gap: "spacing-3",
            pointerEvents: "auto"
          }}
        >
          {onClickClear && value && (
            <Target
              ref={clearButtonRef}
              aria-label="clear"
              tabIndex={-1}
              sx={{
                display: "flex",
                alignItems: "center"
              }}
              onClick={onClickClear}
            >
              <Icon icon="x-circle" size="xs" color="control-enabled" />
            </Target>
          )}
          {icon && (
            <Target
              aria-hidden={onIconClick ? "false" : "true"}
              aria-label={onIconClick ? "click icon" : undefined}
              onClick={handleIconClick}
              sx={{
                display: "flex",
                alignItems: "center"
              }}
            >
              <Icon icon={icon as IconName} size="xs" color="control-enabled" />
            </Target>
          )}

          {searchIcon && (
            <Box
              ref={iconButtonRef}
              sx={{ display: "flex", alignItems: "center" }}
            >
              {searchIcon()}
            </Box>
          )}
        </Box>
      </Box>
      {showDropdown && (
        <DropdownMenu
          dropdownOptions={dropdownOptions}
          showNoResults={showNoResults}
          focusedIdx={focusedOptionIdx}
          optionDisplayCount={optionDisplayCount}
          noResultsMessage={noResultsMessage}
        />
      )}
      {description && (
        <Text variant="body-2-regular" sx={{ ...descriptionTextStyles }}>
          {description}
        </Text>
      )}
      {error && (
        <Text variant="body-2-regular" sx={{ color: "field-text-error" }}>
          {error}
        </Text>
      )}
    </Box>
  );
};
